import React, { useMemo } from 'react';
import {
  HiOutlineArrowTrendingUp,
  HiOutlineBanknotes,
  HiOutlineChartBar,
  HiOutlineClipboardDocumentList,
  HiOutlineDocumentChartBar,
  HiOutlineQrCode,
  HiOutlineUserGroup,
} from 'react-icons/hi2';
import { Link } from 'react-router-dom';
import useAPI from '../../hooks/useAPI';
import endpoints from '../../Services/endpointAdmin';
import { findFieldDeep, getAdminLembagaScope, matchItemToAdminScope } from './adminScope';

function HomeAdmin({ admin }) {
  const kodeHimpun = findFieldDeep(admin, ['kode_himpun', 'kodeHimpun']) || 'Belum terdata';

  const { data: donaturData, loading: loadingDonatur } = useAPI('/api/donatur');
  const { data: transaksiData, loading: loadingTransaksi } = useAPI('/api/transaksi');
  const { data: jenisDonasiData, loading: loadingJenisDonasi } = useAPI(endpoints.jenis_donasi.getAll);

  const donaturArray = useMemo(() => {
    if (Array.isArray(donaturData)) return donaturData;
    if (Array.isArray(donaturData?.donatur)) return donaturData.donatur;
    if (Array.isArray(donaturData?.data)) return donaturData.data;
    if (Array.isArray(donaturData?.result)) return donaturData.result;
    return [];
  }, [donaturData]);

  const transaksiArray = useMemo(() => {
    if (Array.isArray(transaksiData)) return transaksiData;
    if (Array.isArray(transaksiData?.transaksi)) return transaksiData.transaksi;
    if (Array.isArray(transaksiData?.data)) return transaksiData.data;
    if (Array.isArray(transaksiData?.result)) return transaksiData.result;
    return [];
  }, [transaksiData]);

  const lembagaScope = useMemo(() => getAdminLembagaScope(admin), [admin]);
  const scopedTransaksiArray = useMemo(
    () => transaksiArray.filter((trx) => matchItemToAdminScope(trx, lembagaScope)),
    [transaksiArray, lembagaScope]
  );
  const scopedDonaturArray = useMemo(
    () => donaturArray.filter((donatur) => matchItemToAdminScope(donatur, lembagaScope)),
    [donaturArray, lembagaScope]
  );

  const jenisDonasiArray = useMemo(() => {
    if (Array.isArray(jenisDonasiData)) return jenisDonasiData;
    if (Array.isArray(jenisDonasiData?.jenis_donasi)) return jenisDonasiData.jenis_donasi;
    if (Array.isArray(jenisDonasiData?.data)) return jenisDonasiData.data;
    if (Array.isArray(jenisDonasiData?.result)) return jenisDonasiData.result;
    return [];
  }, [jenisDonasiData]);

  const formatRupiah = (angka) => (
    new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(Number(angka) || 0)
  );

  const formatCompactRupiah = (angka) => {
    const value = Number(angka) || 0;
    if (value >= 1000000000) return `Rp ${(value / 1000000000).toFixed(1)} M`;
    if (value >= 1000000) return `Rp ${(value / 1000000).toFixed(1)} Jt`;
    if (value >= 1000) return `Rp ${(value / 1000).toFixed(0)} Rb`;
    return formatRupiah(value);
  };

  const dashboard = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];

    let donaturBaruBulanIni = 0;
    for (const donatur of scopedDonaturArray) {
      const createdAt = donatur.created_at || donatur.tgl_daftar || donatur.tanggal_daftar || donatur.tanggal || donatur.tgl;
      if (!createdAt) continue;
      const date = new Date(createdAt);
      if (!Number.isNaN(date.getTime()) && date.getFullYear() === currentYear && date.getMonth() === currentMonth) {
        donaturBaruBulanIni += 1;
      }
    }

    let totalDanaTerkumpul = 0;
    let totalDanaHariIni = 0;
    let totalDanaBulanIni = 0;
    let pendingCount = 0;
    let processingCount = 0;
    let successCount = 0;
    const monthlyTotals = Array(12).fill(0);
    const categoryTotals = {};
    const recentTransactions = [];

    for (const trx of scopedTransaksiArray) {
      const amount = Number(trx.jumlah || trx.jumlah_donasi || 0) || 0;
      const rawDate = trx.tanggal_transaksi || trx.tgl_transaksi || trx.created_at || trx.tanggal || trx.tgl;
      const status = (trx.status || trx.status_transaksi || trx.statusTransaksi || 'pending').toString().toLowerCase();
      const kodeJenisDonasi =
        trx.kode_jenis_donasi ||
        trx.jenis_donasi?.kode_jenis_donasi ||
        trx.jenisDonasi?.kode_jenis_donasi ||
        trx.jenis_donasi?.kode ||
        trx.jenisDonasi?.kode ||
        '';
      const matchedJenisDonasi = jenisDonasiArray.find((item) => {
        const itemKode =
          item.kode_jenis_donasi ||
          item.kode ||
          item.kodeJenisDonasi ||
          '';
        return itemKode && itemKode === kodeJenisDonasi;
      });
      const category =
        matchedJenisDonasi?.nama_donasi ||
        matchedJenisDonasi?.nama ||
        trx.jenis_donasi?.nama_donasi ||
        trx.jenisDonasi?.nama_donasi ||
        trx.nama_jenis_donasi ||
        'Donasi';
      const donaturName =
        trx.nama_donatur ||
        trx.donatur?.nama ||
        trx.kode_donatur?.nama ||
        trx.kode_donatur ||
        'Donatur';

      if (status === 'pending') pendingCount += 1;
      else if (status === 'diproses' || status === 'processed' || status === 'processing') processingCount += 1;
      else if (status === 'success' || status === 'verified' || status === 'diverifikasi' || status === 'berhasil') successCount += 1;

      if (!rawDate) continue;
      const date = new Date(rawDate);
      if (Number.isNaN(date.getTime())) continue;

      totalDanaTerkumpul += amount;
      if (date.getFullYear() === currentYear) {
        monthlyTotals[date.getMonth()] += amount;
      }
      if (date.getFullYear() === currentYear && date.getMonth() === currentMonth) {
        totalDanaBulanIni += amount;
      }
      if (date.getTime() >= todayStart.getTime()) {
        totalDanaHariIni += amount;
      }

      if (kodeJenisDonasi) {
        categoryTotals[kodeJenisDonasi] = (categoryTotals[kodeJenisDonasi] || 0) + amount;
      }
      recentTransactions.push({
        id: trx.id || trx.kode_transaksi || `${category}-${rawDate}`,
        kode: trx.kode_transaksi || '-',
        donatur: donaturName,
        amount,
        date,
        category,
        status,
      });
    }

    recentTransactions.sort((a, b) => b.date - a.date);

    const topCategories = jenisDonasiArray
      .map((item) => {
        const kode =
          item.kode_jenis_donasi ||
          item.kode ||
          item.kodeJenisDonasi ||
          '';
        const label =
          item.nama_donasi ||
          item.nama ||
          item.nama_jenis ||
          kode ||
          'Donasi';

        return {
          kode,
          label,
          value: categoryTotals[kode] || 0,
        };
      })
      .sort((a, b) => b.value - a.value);

    return {
      totalDonatur: scopedDonaturArray.length,
      donaturBaruBulanIni,
      totalDanaHariIni,
      totalDanaBulanIni,
      totalDanaTerkumpul,
      pendingCount,
      processingCount,
      successCount,
      monthlyTotals,
      monthLabels,
      peakMonthlyValue: Math.max(...monthlyTotals, 1),
      recentTransactions: recentTransactions.slice(0, 3),
      topCategories,
    };
  }, [scopedDonaturArray, scopedTransaksiArray, jenisDonasiArray]);

  const isLoading = loadingDonatur || loadingTransaksi || loadingJenisDonasi;

  const summaryCards = [
    {
      label: 'Dana Terkumpul',
      value: formatCompactRupiah(dashboard.totalDanaTerkumpul),
      note: 'Akumulasi seluruh transaksi',
      icon: HiOutlineBanknotes,
      pill: 'Total',
    },
    {
      label: 'Donasi Bulan Ini',
      value: formatRupiah(dashboard.totalDanaBulanIni),
      note: 'Pemasukan bulan berjalan',
      icon: HiOutlineArrowTrendingUp,
      pill: 'Bulanan',
    },
    {
      label: 'Total Donatur',
      value: `${dashboard.totalDonatur}`,
      note: `+${dashboard.donaturBaruBulanIni} baru bulan ini`,
      icon: HiOutlineUserGroup,
      pill: 'Aktif',
    },
  ];

  const statusCards = [
    {
      label: 'Pending',
      value: dashboard.pendingCount,
      note: 'Menunggu konfirmasi donatur',
      color: 'text-slate-700',
      bg: 'bg-slate-100 border border-slate-200',
    },
    {
      label: 'Diproses',
      value: dashboard.processingCount,
      note: 'Siap diverifikasi admin',
      color: 'text-blue-800',
      bg: 'bg-blue-100 border border-blue-200',
    },
    {
      label: 'Sukses',
      value: dashboard.successCount,
      note: 'Sudah selesai diverifikasi',
      color: 'text-emerald-800',
      bg: 'bg-emerald-100 border border-emerald-200',
    },
  ];

  const quickActions = [
    {
      label: 'Kelola Donasi',
      description: 'Pantau transaksi, lihat detail, dan verifikasi dari satu halaman.',
      to: '/konfirmasi/donasi',
      icon: HiOutlineClipboardDocumentList,
    },
    {
      label: 'Laporan Donasi',
      description: 'Cek rangkuman nominal, kategori, dan metode donasi.',
      to: '/laporan/donasi',
      icon: HiOutlineDocumentChartBar,
    },
    {
      label: 'Kelola QR Code',
      description: 'Atur QRIS aktif yang digunakan donatur saat pembayaran.',
      to: '/transaksi/qrcode',
      icon: HiOutlineQrCode,
    },
  ];

  const todayLabel = new Date().toLocaleDateString('id-ID', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const currentMonthName = new Date().toLocaleDateString('id-ID', {
    month: 'long',
    year: 'numeric',
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-6 py-12">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600" />
        <p className="mt-4 text-sm font-semibold text-slate-700">Memuat dashboard admin...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/70 px-4 sm:px-6 lg:px-12 py-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Page Top Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-b border-slate-200/80 pb-5">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-blue-600">
              MPZ DT • Dashboard Admin
            </p>
            <h1 className="mt-1 text-2xl sm:text-3xl font-extrabold text-slate-900">
              Assalamualaikum, {findFieldDeep(admin, ['nama_user', 'nama', 'name', 'username']) || 'Admin MPZ'}
            </h1>
            <p className="mt-1 text-xs sm:text-sm text-slate-500">
              Kelola donatur, transaksi, dan verifikasi donasi dari satu dashboard yang ringkas.
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-right shadow-sm self-start sm:self-auto">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Hari ini</p>
            <p className="text-xs font-bold text-slate-700 mt-0.5">{todayLabel}</p>
          </div>
        </div>

        {/* Layout Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.7fr_1.1fr] gap-6">
          {/* LEFT COLUMN */}
          <div className="space-y-6">
            {/* Hero Control Center Card */}
            <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-blue-950 rounded-3xl shadow-[0_20px_50px_rgba(15,23,42,0.15)] text-white p-6 sm:p-8">
              <div className="relative z-10 max-w-xl">
                <div className="inline-block rounded-full bg-white/10 backdrop-blur px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-amber-300 mb-3">
                  Pusat Kontrol Admin
                </div>
                <h2 className="text-xl sm:text-2xl font-bold leading-snug">
                  Pantau performa donasi dan status verifikasi dengan cepat.
                </h2>
                <p className="mt-2 text-xs sm:text-sm text-slate-200 leading-relaxed">
                  Kode himpun Anda: <span className="font-bold text-white">{kodeHimpun}</span>. Fokus utama bulan ini adalah menjaga alur pending, diproses, dan sukses tetap tertata rapi.
                </p>

                {/* Summary Cards Inside Hero */}
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {summaryCards.map((item) => {
                    const Icon = item.icon;
                    return (
                      <div key={item.label} className="rounded-2xl border border-white/15 bg-white/10 p-4 backdrop-blur-md transition hover:bg-white/15">
                        <div className="flex items-center justify-between gap-2">
                          <span className="inline-flex items-center rounded-full bg-amber-400/20 px-2 py-0.5 text-[10px] font-bold text-amber-300">
                            {item.pill}
                          </span>
                          <Icon className="w-4 h-4 text-amber-300" />
                        </div>
                        <p className="mt-3 text-[11px] font-medium text-slate-300">{item.label}</p>
                        <p className="text-base sm:text-lg font-bold text-white mt-0.5">
                          {item.value}
                        </p>
                        <p className="text-[10px] text-slate-300 mt-0.5">{item.note}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Recent Transactions Card */}
            <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold text-slate-900">
                    Aktivitas Donasi Terbaru
                  </h3>
                  <p className="text-xs text-slate-500">
                    Transaksi terbaru yang perlu Anda perhatikan.
                  </p>
                </div>
                <span className="text-xs font-bold text-blue-600 bg-blue-50 px-3 py-1 rounded-lg">
                  {currentMonthName}
                </span>
              </div>

              <div className="divide-y divide-slate-100">
                {dashboard.recentTransactions.length === 0 ? (
                  <div className="px-4 py-8 text-center text-xs text-slate-500">
                    Belum ada transaksi terbaru.
                  </div>
                ) : (
                  dashboard.recentTransactions.map((trx) => {
                    const isSuccess = trx.status === 'success' || trx.status === 'verified' || trx.status === 'diverifikasi' || trx.status === 'berhasil';
                    const isProcessing = trx.status === 'diproses' || trx.status === 'processed' || trx.status === 'processing';
                    const statusText = isSuccess ? 'Sukses' : isProcessing ? 'Diproses' : 'Pending';

                    return (
                      <div
                        key={trx.id}
                        className="px-5 py-3.5 flex items-center justify-between text-xs sm:text-sm hover:bg-slate-50 transition"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-700">
                            TR
                          </div>
                          <div>
                            <p className="font-bold text-slate-900">{trx.donatur}</p>
                            <p className="text-xs text-slate-500 mt-0.5">
                              {trx.category} • {trx.date.toLocaleDateString('id-ID', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                              })}
                            </p>
                          </div>
                        </div>

                        <div className="text-right">
                          <p className="font-bold text-slate-900">{formatRupiah(trx.amount)}</p>
                          <span
                            className={`inline-block mt-0.5 rounded-full px-2.5 py-0.5 text-[11px] font-bold ${
                              isSuccess
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                : isProcessing
                                ? 'bg-blue-50 text-blue-700 border border-blue-200'
                                : 'bg-slate-100 text-slate-600 border border-slate-200'
                            }`}
                          >
                            {statusText}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </section>

            {/* Quick Actions Shortcuts */}
            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4">
              <div>
                <h3 className="text-sm font-bold text-slate-900">
                  Akses Cepat Admin
                </h3>
                <p className="text-xs text-slate-500">
                  Shortcut ke halaman yang paling sering dipakai saat operasional.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {quickActions.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.label}
                      to={item.to}
                      className="flex flex-col justify-between rounded-xl border border-slate-200 bg-slate-50/70 p-4 transition hover:bg-white hover:border-blue-300 hover:shadow-sm"
                    >
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                            <Icon className="h-5 w-5" />
                          </div>
                          <span className="text-xs font-bold text-blue-600">Buka →</span>
                        </div>
                        <h4 className="text-xs sm:text-sm font-bold text-slate-900">{item.label}</h4>
                        <p className="text-xs text-slate-500 leading-relaxed">{item.description}</p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>
          </div>

          {/* RIGHT COLUMN */}
          <div className="space-y-6">
            {/* Status Breakdown */}
            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-blue-600">Ringkasan Status</p>
                  <h3 className="text-sm font-bold text-slate-900">
                    Progres Verifikasi
                  </h3>
                </div>
                <div className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-bold text-slate-700">
                  <HiOutlineChartBar className="w-3.5 h-3.5 text-slate-500" />
                  Monitoring
                </div>
              </div>

              <div className="space-y-3">
                {statusCards.map((item) => (
                  <div key={item.label} className="rounded-xl border border-slate-200 p-3.5 hover:bg-slate-50 transition-colors">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-xs font-bold text-slate-900">{item.label}</p>
                        <p className="text-[11px] text-slate-500">{item.note}</p>
                      </div>
                      <span className={`inline-flex min-w-8 items-center justify-center rounded-full px-2.5 py-0.5 text-xs font-bold ${item.bg} ${item.color}`}>
                        {item.value}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Monthly Trend Chart Section */}
            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-slate-900">
                  Distribusi Donasi per Bulan
                </h3>
                <span className="text-[11px] font-bold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-lg">
                  Statistik
                </span>
              </div>

              <div className="rounded-xl bg-slate-50/70 border border-slate-100 px-3 py-4">
                <div className="flex items-end gap-2 h-44 border-b border-slate-200 pb-2">
                  {dashboard.monthlyTotals.map((value, index) => {
                    const height = Math.max((value / dashboard.peakMonthlyValue) * 100, value > 0 ? 8 : 2);
                    return (
                      <div key={dashboard.monthLabels[index]} className="flex-1 flex flex-col items-center justify-end h-full group">
                        <div className="w-full flex items-end justify-center h-36 relative">
                          <div
                            className="w-full max-w-[24px] rounded-t-md bg-blue-600 transition-all duration-200 group-hover:bg-blue-500"
                            style={{ height: `${height}%` }}
                            title={`${dashboard.monthLabels[index]}: ${formatRupiah(value)}`}
                          />
                        </div>
                        <span className="mt-2 text-[10px] font-bold text-slate-600">{dashboard.monthLabels[index]}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Top Categories Progress Bars */}
            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-3">
              <h3 className="text-sm font-bold text-slate-900">
                Kategori Donasi Utama
              </h3>
              <div className="space-y-3">
                {dashboard.topCategories.length === 0 ? (
                  <div className="py-6 text-center text-xs text-slate-400">
                    Belum ada data kategori donasi.
                  </div>
                ) : (
                  dashboard.topCategories.map((category) => {
                    const percentage = dashboard.totalDanaTerkumpul > 0
                      ? Math.min((category.value / dashboard.totalDanaTerkumpul) * 100, 100)
                      : 0;

                    return (
                      <div key={category.label} className="rounded-xl border border-slate-200 p-3 hover:bg-slate-50 transition-colors space-y-1.5">
                        <div className="flex items-center justify-between text-xs font-semibold">
                          <span className="text-slate-900">{category.label}</span>
                          <span className="text-slate-500 font-bold">{percentage.toFixed(1)}%</span>
                        </div>
                        <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-blue-600 transition-all duration-300"
                            style={{ width: `${Math.max(percentage, 4)}%` }}
                          />
                        </div>
                        <p className="text-[11px] font-medium text-slate-500 text-right">{formatCompactRupiah(category.value)}</p>
                      </div>
                    );
                  })
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HomeAdmin;
